/* The skills hub's fixed vocabulary — sources, sorts, auditors — and the pure helpers
   its views format with: counts, sizes, the file tree, and the pieces of a SKILL.md.
   The skills themselves come from `/skills`; the audit roll-up is `auditSummary` in
   `shared/components/skill-atoms.tsx`. */

import type { SkillFile, SkillSort, SkillSource } from "@/shared/types";

export const SKILL_SOURCES: { key: "all" | SkillSource; label: string }[] = [
  { key: "all", label: "All" },
  { key: "workspace", label: "Workspace" },
  { key: "definable", label: "Definable" },
  { key: "official", label: "Official" },
  { key: "community", label: "Community" },
];

export const SORTS: { key: SkillSort; label: string }[] = [
  { key: "stars", label: "Most starred" },
  { key: "name", label: "A–Z" },
];

/* skills.sh's audit partners, in the order the table shows them. */
export const AUDITORS: { key: string; label: string }[] = [
  { key: "agent-trust-hub", label: "Gen Agent Trust Hub" },
  { key: "socket", label: "Socket" },
  { key: "snyk", label: "Snyk" },
  { key: "runlayer", label: "Runlayer" },
  { key: "zeroleaks", label: "ZeroLeaks" },
];

/* Agents that load SKILL.md folders — where `npx skills add` can install one. */
export const WORKS_WITH = ["Claude Code", "Cursor", "Codex", "VS Code", "Windsurf"];

export function installCommand(repo: string, slug: string): string {
  return `npx skills add ${repo} --skill ${slug}`;
}

export function formatCount(n: number | null): string | null {
  if (n == null) return null;
  if (n >= 1000000) return `${(n / 1000000).toFixed(1).replace(/\.0$/, "")}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1).replace(/\.0$/, "")}K`;
  return String(n);
}

export function formatBytes(n: number): string {
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  if (n >= 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${n} B`;
}

/* ------------------------------- files ----------------------------------- */

export type FileKind = "markdown" | "text" | "image" | "binary";

const IMAGE = /\.(png|jpe?g|gif|webp|svg)$/i;

/** How the viewer shows a file. An SVG arrives as text (the server never serves one as
    an image) and is shown as one — inside an <img>, where its scripts cannot run. */
export function fileKind(path: string, isText: boolean): FileKind {
  if (IMAGE.test(path) && (!isText || /\.svg$/i.test(path))) return "image";
  if (!isText) return "binary";
  return /\.(md|mdx|markdown)$/i.test(path) ? "markdown" : "text";
}

export interface FileNode {
  name: string;
  /** The folder's or file's path relative to the skill. */
  path: string;
  file?: SkillFile;
  children?: FileNode[];
}

/** The flat file list as a tree: folders first, then files, each by name — except
    that SKILL.md leads, because it is where every reader starts. */
export function buildTree(files: SkillFile[]): FileNode[] {
  const root: FileNode = { name: "", path: "", children: [] };
  for (const file of files) {
    let node = root;
    const parts = file.path.split("/");
    parts.forEach((part, i) => {
      const path = parts.slice(0, i + 1).join("/");
      if (i === parts.length - 1) {
        node.children!.push({ name: part, path, file });
        return;
      }
      let dir = node.children!.find((c) => c.children && c.name === part);
      if (!dir) node.children!.push((dir = { name: part, path, children: [] }));
      node = dir;
    });
  }
  const rank = (n: FileNode) => (n.path === "SKILL.md" ? 0 : n.children ? 1 : 2);
  const sort = (nodes: FileNode[]): FileNode[] =>
    nodes
      .sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name))
      .map((n) => (n.children ? { ...n, children: sort(n.children) } : n));
  return sort(root.children!);
}

/* ----------------------------- SKILL.md ---------------------------------- */

/** A markdown file's YAML frontmatter apart from its body. */
export function splitFrontmatter(text: string): { frontmatter: string | null; body: string } {
  const m = text.replace(/^﻿/, "").match(/^---[ \t]*\r?\n([\s\S]*?)\r?\n---[ \t]*(?:\r?\n|$)/);
  return m ? { frontmatter: m[1], body: text.slice(text.indexOf(m[0]) + m[0].length) } : { frontmatter: null, body: text };
}
